<!-- src/routes/admin.svelte -->
<script>
  import { onMount } from 'svelte';
  import {
    getAuth, GoogleAuthProvider, signInWithRedirect, signInWithPopup,
    getRedirectResult, signOut, onAuthStateChanged
  } from 'firebase/auth';
  import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
  import { app } from '../lib/firebase.js';
  import { set_meta } from '../lib/seo.js';

  let user = null;
  let auth_loading = true;
  let contacts_loading = false;
  let redirecting = false;
  let contacts = [];
  let error = '';

  const auth = getAuth(app);
  const is_local = typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  // Lazily create the Functions instance (connect to emulator only on localhost).
  let functions_instance;
  function get_functions_instance() {
    if (!functions_instance) {
      functions_instance = getFunctions(app);
      if (is_local) {
        connectFunctionsEmulator(functions_instance, 'localhost', 5001);
      }
    }
    return functions_instance;
  }

  async function load_contacts() {
    contacts_loading = true;
    error = '';
    try {
      const list = httpsCallable(get_functions_instance(), 'listContacts');
      const result = await list();
      contacts = (result.data && result.data.contacts) || [];
    } catch (e) {
      if (e && e.code === 'functions/permission-denied') {
        error = 'This account is not authorized to view submissions.';
      } else {
        error = (e && e.message) || 'Could not load submissions.';
      }
      contacts = [];
    } finally {
      contacts_loading = false;
    }
  }

  async function start_google_signin() {
    error = '';
    try {
      const provider = new GoogleAuthProvider();
      if (is_local) {
        // Popup is simpler for local dev (no auth-handler domain round-trip).
        await signInWithPopup(auth, provider);
      } else {
        redirecting = true;
        await signInWithRedirect(auth, provider);
      }
    } catch (e) {
      redirecting = false;
      error = (e && e.message) || 'Sign-in failed.';
    }
  }

  async function sign_out_now() {
    await signOut(auth);
    contacts = [];
    error = '';
  }

  function fmt_date(iso) {
    if (!iso) return '';
    try { return new Date(iso).toLocaleString(); } catch (_) { return iso; }
  }

  onMount(() => {
    set_meta({ title: 'Admin — Contact Submissions', path: '/admin' });

    // Keep the admin page out of search indexes.
    const robots = document.createElement('meta');
    robots.name = 'robots';
    robots.content = 'noindex, nofollow';
    document.head.appendChild(robots);

    // Surface any error coming back from a redirect sign-in.
    getRedirectResult(auth).catch((e) => { error = (e && e.message) || ''; });

    const unsub = onAuthStateChanged(auth, (u) => {
      user = u;
      auth_loading = false;
      if (u) {
        sessionStorage.removeItem('admin_redirect');
        load_contacts();
      } else {
        contacts = [];
        // In production, send a logged-out visitor straight to Google sign-in
        // (once per session, so a cancel doesn't loop).
        if (!is_local && !sessionStorage.getItem('admin_redirect')) {
          sessionStorage.setItem('admin_redirect', '1');
          start_google_signin();
        }
      }
    });
    return unsub;
  });
</script>

<section class="admin">
  <div class="container">
    <h1 class="marker-font">Contact submissions</h1>

    {#if auth_loading || redirecting}
      <p class="muted">{redirecting ? 'Redirecting to Google sign-in…' : 'Loading…'}</p>
    {:else if !user}
      <p class="muted">Sign in with your Google account to view contact-form submissions.</p>
      <button class="btn" on:click={start_google_signin}>Sign in with Google</button>
      {#if error}<p class="error">{error}</p>{/if}
    {:else}
      <div class="signed_in">
        <span class="muted">Signed in as {user.email}</span>
        <button class="btn ghost" on:click={sign_out_now}>Sign out</button>
      </div>

      {#if error}
        <p class="error">{error}</p>
      {:else if contacts_loading}
        <p class="muted">Loading submissions…</p>
      {:else if contacts.length === 0}
        <p class="muted">No submissions yet.</p>
      {:else}
        <p class="muted">{contacts.length} submission{contacts.length === 1 ? '' : 's'}</p>
        <div class="table_wrap">
          <table>
            <thead>
              <tr><th>Date</th><th>Name</th><th>Email</th><th>Message</th></tr>
            </thead>
            <tbody>
              {#each contacts as c (c.id)}
                <tr>
                  <td class="nowrap">{fmt_date(c.createdAt)}</td>
                  <td>{c.name}</td>
                  <td><a href="mailto:{c.email}">{c.email}</a></td>
                  <td class="msg">{c.message}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
    {/if}
  </div>
</section>

<style>
  .admin {
    padding: 6rem 2rem;
    background: var(--color-background);
    min-height: 100vh;
  }

  .container {
    max-width: 1100px;
    margin: 0 auto;
  }

  h1 {
    font-size: 2.5rem;
    color: var(--color-text);
    margin-bottom: 1.5rem;
  }

  .muted {
    color: var(--color-text-muted);
    margin: 0 0 1rem;
  }

  .error {
    color: var(--color-secondary);
    margin: 1rem 0;
  }

  .btn {
    padding: 0.75rem 1.5rem;
    border: none;
    border-radius: 6px;
    background: var(--color-primary);
    color: var(--color-background);
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btn:hover {
    background: var(--color-secondary);
  }

  .btn.ghost {
    background: transparent;
    border: 1px solid var(--color-card-hover);
    color: var(--color-text);
  }

  .signed_in {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid var(--color-card-hover);
  }

  .table_wrap {
    overflow-x: auto;
    border: 1px solid var(--color-card-hover);
    border-radius: 12px;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    background: var(--color-card-bg);
  }

  th, td {
    text-align: left;
    padding: 0.85rem 1rem;
    border-bottom: 1px solid var(--color-card-hover);
    vertical-align: top;
    font-size: 0.95rem;
    color: var(--color-text);
  }

  th {
    color: var(--color-text-muted);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-size: 0.8rem;
  }

  td a {
    color: var(--color-primary);
  }

  .nowrap {
    white-space: nowrap;
    color: var(--color-text-muted);
  }

  .msg {
    max-width: 480px;
    white-space: pre-wrap;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
</style>
